import React, { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import axios from 'axios';
import BaseURL from '../../../assets/contants/BaseURL';
import { CHART_VARIANTS, getChartDemoData } from '../chartVariants';
import useMeterFieldSeries from '../useMeterFieldSeries';
import useMeterFieldBreakdown from '../useMeterFieldBreakdown';
import useMeterFieldHeatmap from '../useMeterFieldHeatmap';

const DEFAULT_COLORS = ['#4318FF', '#00B5D8', '#22C55E', '#f87171', '#fbbf24'];

function zeroSeries(value) {
    if (typeof value === 'number') return 0;
    if (Array.isArray(value)) return value.map(zeroSeries);
    if (value && typeof value === 'object') {
        const out = { ...value };
        if (Array.isArray(out.data)) out.data = zeroSeries(out.data);
        if (typeof out.y === 'number') out.y = 0;
        return out;
    }
    return value;
}

// Each chart "family" needs a different live-data shape, so each gets its
// own hook (all skipped unless the widget's family/variant actually needs
// it): xy = time series (also reused, twice, for the points/scatter family's
// two-field correlation plot), labeled/radar = current phase breakdown,
// heatmap = 7-day x hour-of-day grid. See useMeterFieldSeries.js,
// useMeterFieldBreakdown.js and useMeterFieldHeatmap.js for the matching
// contract each one follows (title -> field, same as gauges).
const ChartWidget = ({ config }) => {
    const { dataSource = {}, settings = {}, variant = 'line', title } = config;
    const [remoteData, setRemoteData] = useState(null);
    const variantMeta = CHART_VARIANTS.find((v) => v.id === variant) || CHART_VARIANTS[0];
    const { family } = variantMeta;
    const isXY = family === 'xy';
    const isLabeled = family === 'labeled';
    const isRadar = family === 'radar';
    const isPoints = family === 'points';
    const isHeatmap = family === 'heatmap';
    const isPreview = typeof config.id === 'string' && config.id.startsWith('preview-');
    const hasEndpoint = Boolean(dataSource.endpoint);

    const series = useMeterFieldSeries(title, {
        meterId: settings.meterId,
        skip: hasEndpoint || isPreview || !(isXY || isPoints),
    });
    const compareSeries = useMeterFieldSeries(settings.compareFieldTitle, {
        meterId: settings.meterId,
        skip: hasEndpoint || isPreview || !isPoints,
    });
    const breakdown = useMeterFieldBreakdown(title, {
        meterId: settings.meterId,
        skip: hasEndpoint || isPreview || !(isLabeled || isRadar),
    });
    const heatmap = useMeterFieldHeatmap(title, {
        meterId: settings.meterId,
        skip: hasEndpoint || isPreview || !isHeatmap,
    });

    useEffect(() => {
        if (!dataSource.endpoint) return;
        let cancelled = false;
        const token = sessionStorage.getItem('token');

        axios.get(`${BaseURL}${dataSource.endpoint}`, {
            headers: { Authorization: `Token ${token}` },
        })
            .then((res) => { if (!cancelled) setRemoteData(res.data); })
            .catch((err) => console.error('ChartWidget: failed to fetch', err));

        return () => { cancelled = true; };
    }, [dataSource.endpoint]);

    const demo = useMemo(() => getChartDemoData(variant), [variant]);

    const fallbackSeries = useMemo(
        () => (isPreview ? demo.series : zeroSeries(demo.series)),
        [demo.series, isPreview],
    );
    const staticSeries = dataSource.series || fallbackSeries;

    const reportSeries = useMemo(() => {
        if (isXY) {
            if (!series.field) return null;
            return [{ name: series.field.label, data: series.values }];
        }
        if (isPoints) {
            if (!series.field || !compareSeries.field) return null;
            const len = Math.min(series.values.length, compareSeries.values.length);
            const points = [];
            for (let i = 0; i < len; i += 1) {
                points.push(
                    variantMeta.apexType === 'bubble'
                        ? [series.values[i], compareSeries.values[i], 10]
                        : [series.values[i], compareSeries.values[i]],
                );
            }
            return [{ name: `${series.field.label} vs ${compareSeries.field.label}`, data: points }];
        }
        if (isLabeled) {
            return breakdown.group ? breakdown.values : null;
        }
        if (isRadar) {
            return breakdown.group ? [{ name: breakdown.group.label, data: breakdown.values }] : null;
        }
        if (isHeatmap) {
            return heatmap.field && heatmap.series.length ? heatmap.series : null;
        }
        return null;
    }, [
        isXY, isPoints, isLabeled, isRadar, isHeatmap, variantMeta.apexType,
        series.field, series.values, compareSeries.field, compareSeries.values,
        breakdown.group, breakdown.values, heatmap.field, heatmap.series,
    ]);

    const reportCategories = useMemo(() => {
        if (isXY) return series.field ? series.categories : null;
        if (isRadar) return breakdown.group ? breakdown.labels : null;
        return null;
    }, [isXY, isRadar, series.field, series.categories, breakdown.group, breakdown.labels]);

    const reportLabels = useMemo(() => (
        isLabeled && breakdown.group ? breakdown.labels : null
    ), [isLabeled, breakdown.group, breakdown.labels]);

    const rawSeries = remoteData?.series || reportSeries || staticSeries;

    const chartSeries = useMemo(() => (Array.isArray(rawSeries) ? rawSeries : []), [rawSeries]);
    const categories = remoteData?.categories || reportCategories || dataSource.categories || demo.categories;
    const labels = remoteData?.labels || reportLabels || dataSource.labels || demo.labels;

    const options = useMemo(() => {

        const dataLabelStyle = settings.fontSize || settings.textColor
            ? { style: { fontSize: settings.fontSize ? `${settings.fontSize}px` : undefined, colors: settings.textColor ? [settings.textColor] : undefined } }
            : null;

        const axisLabelStyle = settings.fontSize ? { labels: { style: { fontSize: `${settings.fontSize}px` } } } : null;
        const base = {
            chart: {
                toolbar: { show: isXY, tools: { download: false, selection: false, zoom: isXY, zoomin: isXY, zoomout: isXY, pan: isXY, reset: isXY } },
                zoom: { enabled: isXY },
                foreColor: settings.textColor || undefined,
            },
            colors: settings.colors || (settings.color ? [settings.color, ...DEFAULT_COLORS.slice(1)] : DEFAULT_COLORS),
            legend: { show: isLabeled || chartSeries.length > 1, fontSize: settings.fontSize ? `${settings.fontSize}px` : undefined },
            tooltip: { enabled: true, shared: isXY && chartSeries.length > 1 },
        };

        switch (variantMeta.family) {
            case 'labeled':
                return { ...base, labels: labels || [], dataLabels: { enabled: true, ...dataLabelStyle } };
            case 'radar':
                return { ...base, xaxis: { categories: categories || [], ...axisLabelStyle }, dataLabels: { enabled: false } };
            case 'points':
                return { ...base, xaxis: { type: 'numeric', ...axisLabelStyle }, dataLabels: { enabled: false }, grid: { strokeDashArray: 4 } };
            case 'heatmap':
                return { ...base, dataLabels: { enabled: false } };
            case 'xy':
            default: {
                const xyOptions = {
                    ...base,
                    dataLabels: { enabled: false },
                    stroke: { curve: 'smooth', width: variantMeta.apexType === 'bar' ? 0 : 2 },
                    xaxis: { categories: categories || [], ...axisLabelStyle },
                    grid: { strokeDashArray: 4 },
                };
                if (variantMeta.apexType === 'bar') {
                    xyOptions.plotOptions = { bar: { horizontal: Boolean(variantMeta.horizontal) } };
                }
                if (variantMeta.id === 'combo' && chartSeries.length > 1) {
                    xyOptions.stroke = { curve: 'smooth', width: chartSeries.map((s) => (s.type === 'column' ? 0 : 3)) };
                }
                return xyOptions;
            }
        }
    }, [variantMeta, categories, labels, isLabeled, isXY, chartSeries, settings.colors, settings.color, settings.textColor, settings.fontSize]);

    return (
        <div className="chart-widget">

            <Chart
                key={variant}
                options={options}
                series={chartSeries}
                type={variantMeta.apexType}
                width={settings.contentWidth || '100%'}
                height={settings.contentHeight || '100%'}
            />
        </div>
    );
};

export default ChartWidget;
